import { useRef, useState, type FormEvent } from 'react';
import { ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth, type PendingSmsCode, type SecondFactorChallenge } from '@/auth';
import type { SecondFactorKind } from '@/auth';
import { messages } from '@/i18n';
import { CodeField, ErrorAlert } from './mfaShared';
import { isCompleteCode, secondFactorErrorMessage } from './mfaUtils';

/**
 * The second step of a sign-in or a re-authentication.
 *
 * Offers the TOTP factor first whenever one is enrolled, and SMS behind a
 * "Use SMS instead" link — SMS is the recovery path, and the API refuses an
 * SMS sign-in anything but re-enrolling TOTP. `preferKind` flips that for the
 * one screen where the user has just told us their authenticator is gone.
 *
 * Which factors exist comes from the challenge itself, which is display data:
 * the server decides what each one is allowed to do.
 */
export function SecondFactorChallengeForm({
  challenge,
  onSuccess,
  onCancel,
  cancelLabel = messages.mfa.cancel,
  preferKind = 'totp',
}: {
  challenge: SecondFactorChallenge;
  /** Called once the provider accepts the code. */
  onSuccess?: () => void;
  onCancel: () => void;
  cancelLabel?: string;
  preferKind?: SecondFactorKind | undefined;
}) {
  const t = messages.mfa;
  const auth = useAuth();
  const totp = challenge.factors.find((f) => f.kind === 'totp');
  const sms = challenge.factors.find((f) => f.kind === 'sms');
  const initialKind: SecondFactorKind =
    preferKind === 'sms' ? (sms ? 'sms' : 'totp') : totp ? 'totp' : 'sms';

  const [kind, setKind] = useState<SecondFactorKind>(initialKind);
  const [code, setCode] = useState('');
  const [pendingSms, setPendingSms] = useState<PendingSmsCode | null>(null);
  const [busy, setBusy] = useState<'sending' | 'verifying' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const recaptchaRef = useRef<HTMLDivElement>(null);

  const sendSms = () => {
    if (!sms || !recaptchaRef.current) return;
    setError(null);
    setCode('');
    setBusy('sending');
    auth
      .sendSignInSms(challenge, sms.id, recaptchaRef.current)
      .then(setPendingSms)
      .catch((err: unknown) => setError(secondFactorErrorMessage(err)))
      .finally(() => setBusy(null));
  };

  const switchTo = (next: SecondFactorKind) => {
    setKind(next);
    setCode('');
    setError(null);
    if (next === 'sms' && !pendingSms) sendSms();
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!isCompleteCode(code)) return;
    setError(null);
    setBusy('verifying');

    const verify =
      kind === 'totp' && totp
        ? auth.completeSignInWithTotp(challenge, totp.id, code)
        : pendingSms
          ? auth.completeSignInWithSms(pendingSms, code)
          : Promise.reject(new Error('No SMS code has been sent.'));

    verify
      .then(() => {
        setBusy(null);
        onSuccess?.();
      })
      .catch((err: unknown) => {
        setError(secondFactorErrorMessage(err));
        setCode('');
        setBusy(null);
      });
  };

  const waitingForSms = kind === 'sms' && !pendingSms;
  // Nothing is sent until the user asks: each SMS costs money, and a send on
  // mount would fire twice under StrictMode.
  const smsBody = waitingForSms
    ? sms?.maskedPhone
      ? t.challengeSmsSendPrompt.replace('{phone}', sms.maskedPhone)
      : t.challengeSmsSendPromptNoPhone
    : sms?.maskedPhone
      ? t.challengeSmsBody.replace('{phone}', sms.maskedPhone)
      : t.challengeSmsBodyNoPhone;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
      <ErrorAlert message={error} />

      <p className="text-muted-foreground text-sm">
        {kind === 'totp' ? t.challengeTotpBody : busy === 'sending' ? t.sendingSms : smsBody}
      </p>

      <CodeField value={code} onChange={setCode} disabled={busy !== null || waitingForSms} />

      <Button
        type="submit"
        className="w-full"
        disabled={busy !== null || waitingForSms || !isCompleteCode(code)}
      >
        <ShieldCheck aria-hidden />
        {busy === 'verifying' ? t.verifying : t.verify}
      </Button>

      <div className="flex flex-col items-center gap-2 text-xs">
        {kind === 'totp' && sms ? (
          <button
            type="button"
            className="text-accent hover:underline"
            onClick={() => switchTo('sms')}
            disabled={busy !== null}
          >
            {t.useSmsInstead}
          </button>
        ) : null}
        {kind === 'sms' && pendingSms ? (
          <button
            type="button"
            className="text-accent hover:underline"
            onClick={sendSms}
            disabled={busy !== null}
          >
            {t.resendSms}
          </button>
        ) : null}
        {kind === 'sms' && !pendingSms && busy === null ? (
          <button type="button" className="text-accent hover:underline" onClick={sendSms}>
            {t.sendCode}
          </button>
        ) : null}
        {kind === 'sms' && totp ? (
          <button
            type="button"
            className="text-accent hover:underline"
            onClick={() => switchTo('totp')}
            disabled={busy !== null}
          >
            {t.useTotpInstead}
          </button>
        ) : null}
        <button
          type="button"
          className="text-muted-foreground hover:underline"
          onClick={onCancel}
          disabled={busy === 'verifying'}
        >
          {cancelLabel}
        </button>
      </div>

      {/* SMS only: the provider renders an invisible reCAPTCHA in here. */}
      <div ref={recaptchaRef} />
    </form>
  );
}
